import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const NAV_ITEMS = {
  owner: [
    { section: 'Command' },
    { path: '/overwatch', icon: '\uD83D\uDC41', label: 'Overwatch' },
    { path: '/owner', icon: '\uD83C\uDFE0', label: 'Owner Dashboard' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Operations' },
    { section: 'Farm' },
    { path: '/fields', icon: '\uD83C\uDF3E', label: 'Fields & Sites' },
    { path: '/equipment', icon: '\uD83D\uDE9C', label: 'Equipment' },
    { path: '/crew', icon: '\uD83D\uDC77', label: 'Crew' },
    { path: '/operations', icon: '\uD83D\uDCCB', label: 'Work Orders' },
    { path: '/maintenance', icon: '\uD83D\uDD27', label: 'Maintenance' },
    { section: 'Financial' },
    { path: '/payroll', icon: '\uD83D\uDCB0', label: 'Payroll' },
    { path: '/expenses', icon: '\uD83E\uDDFE', label: 'Expenses' },
    { path: '/analytics', icon: '\uD83D\uDCC8', label: 'Analytics' },
    { section: 'Logistics' },
    { path: '/weather', icon: '\u26C5', label: 'Weather' },
    { path: '/logistics', icon: '\uD83D\uDE9A', label: 'Site Moves' },
    { path: '/reports', icon: '\uD83D\uDCC4', label: 'Reports' },
    { section: 'System' },
    { path: '/profile', icon: '\uD83D\uDC64', label: 'Profile' },
  ],
  field_manager: [
    { section: 'Operations' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { path: '/operations', icon: '\uD83D\uDCCB', label: 'Work Orders' },
    { path: '/fields', icon: '\uD83C\uDF3E', label: 'Fields' },
    { path: '/equipment', icon: '\uD83D\uDE9C', label: 'Equipment' },
    { path: '/crew', icon: '\uD83D\uDC77', label: 'Crew' },
    { path: '/maintenance', icon: '\uD83D\uDD27', label: 'Maintenance' },
    { section: 'Financial' },
    { path: '/payroll', icon: '\uD83D\uDCB0', label: 'Payroll' },
    { section: 'Logistics' },
    { path: '/weather', icon: '\u26C5', label: 'Weather' },
    { path: '/logistics', icon: '\uD83D\uDE9A', label: 'Site Moves' },
    { path: '/reports', icon: '\uD83D\uDCC4', label: 'Reports' },
    { path: '/profile', icon: '\uD83D\uDC64', label: 'Profile' },
  ],
  operator: [
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'My Day' },
    { path: '/equipment', icon: '\uD83D\uDE9C', label: 'Equipment' },
    { path: '/weather', icon: '\u26C5', label: 'Weather' },
    { path: '/profile', icon: '\uD83D\uDC64', label: 'Profile' },
  ],
  mechanic: [
    { path: '/maintenance', icon: '\uD83D\uDD27', label: 'Maintenance' },
    { path: '/equipment', icon: '\uD83D\uDE9C', label: 'Equipment' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { path: '/profile', icon: '\uD83D\uDC64', label: 'Profile' },
  ],
  logistics: [
    { path: '/logistics', icon: '\uD83D\uDE9A', label: 'Site Moves' },
    { path: '/equipment', icon: '\uD83D\uDE9C', label: 'Equipment' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { path: '/profile', icon: '\uD83D\uDC64', label: 'Profile' },
  ],
  demo: [
    { section: 'Overview' },
    { path: '/owner', icon: '\uD83C\uDFE0', label: 'Owner Dashboard' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Operations' },
    { path: '/fields', icon: '\uD83C\uDF3E', label: 'Fields & Sites' },
    { path: '/equipment', icon: '\uD83D\uDE9C', label: 'Equipment' },
    { path: '/crew', icon: '\uD83D\uDC77', label: 'Crew' },
    { path: '/analytics', icon: '\uD83D\uDCC8', label: 'Analytics' },
    { path: '/reports', icon: '\uD83D\uDCC4', label: 'Reports' },
    { path: '/profile', icon: '\uD83D\uDC64', label: 'Profile' },
  ],
};

// Complete IQ Core Team — full owner access
const COMPLETE_IQ_TEAM = [
  'iod-master@islandoverheaddoors.com',
  'rmiskulin@gmail.com',
  'threewayindustries@gmail.com',
];

// Geerts Farms owner
const GEERTS_OWNER = 'bcgeerts@execulink.com';

const OVERWATCH_TEAM = [
  'iod-master@islandoverheaddoors.com',
  'rmiskulin@gmail.com',
  'tyler.hodgson32@gmail.com',
  'bcgeerts@execulink.com',
];

function getEffectiveRole(user) {
  if (!user) return 'operator';
  const email = user.email?.toLowerCase();
  if (COMPLETE_IQ_TEAM.map(e => e.toLowerCase()).includes(email)) return 'owner';
  if (email === GEERTS_OWNER) return 'owner';
  if (email === 'investor@completeiq.com') return 'demo';
  return user.role || 'operator';
}

export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const role = getEffectiveRole(user);
  let navItems = NAV_ITEMS[role] || NAV_ITEMS.owner;
  if (!OVERWATCH_TEAM.map(e => e.toLowerCase()).includes(user?.email?.toLowerCase())) {
    navItems = navItems.filter(item => item.path !== '/overwatch');
  }
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('') || user?.email?.[0]?.toUpperCase() || 'U';
  const companyLabel = 'Geerts Farms Ltd.';

  return (
    <div className="app-layout">
      <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFD100', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
              {'\uD83C\uDF3E'}
            </div>
            <div>
              <div className="sidebar-logo">Geerts Farms</div>
              <div className="sidebar-sub">CropPatrol</div>
            </div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="sidebar-section">{item.section}</div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{initials}</div>
            <div>
              <div className="user-name">{user?.full_name || user?.email}</div>
              <div className="user-role">{role}</div>
            </div>
          </div>
          <button
            className="btn btn-outline btn-sm"
            style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}
            onClick={() => { signOut(); navigate('/login'); }}
          >
            Sign Out
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
